/**
 * Phase 3 - tokenRefresh: トークン定期リフレッシュループ
 *
 * 認証済みの場合は expiresAt の 60 秒前に自動的にリフレッシュを行い、
 * 成功したら再び waiting に戻る。
 * - 4xx（TokenRefreshClientError）: loggedOut に遷移（セッション期限切れ・再ログインが必要）
 * - ネットワーク障害（TypeError）・5xx（TokenRefreshServerError）: retryWaiting に遷移
 *   （一時エラー。ページコンテンツは表示されたまま PAGE_VISIBLE / UNAUTHORIZED_401 で再試行）
 * XState がステート離脱時にタイマーを自動キャンセルするためメモリリークなし。
 *
 * バックグラウンドタブではブラウザがタイマーを抑制することがあるため、
 * PAGE_VISIBLE イベントでタブがフォアグラウンドに戻ったとき、
 * トークンが期限切れ（または 60 秒以内に期限切れ）なら即座にリフレッシュを開始する。
 */
export const phase3TokenRefreshConfig = {
  initial: 'checking' as const,
  states: {
    checking: {
      always: [{ target: 'waiting' as const, guard: 'isAuthenticated' as const }, { target: 'notRequired' as const }],
    },
    waiting: {
      // expiresAt の 60 秒前に自動遷移
      after: { preRefreshDelay: 'refreshing' as const },
      on: {
        // バックグラウンドタブではタイマーが抑制されることがあるため、
        // タブがフォアグラウンドに戻ったときにトークンの有効性を確認する
        PAGE_VISIBLE: {
          target: 'refreshing' as const,
          guard: 'isTokenExpiredOrExpiring' as const,
        },
        // 認証付き API が 401 を返した場合、トークンが期限切れの可能性があるため即座にリフレッシュする
        UNAUTHORIZED_401: 'refreshing' as const,
      },
    },
    refreshing: {
      invoke: {
        src: 'refreshToken' as const,
        onDone: {
          target: 'waiting' as const,
          actions: 'saveRefreshedToken' as const,
        },
        onError: [
          // 4xx（セッション期限切れ）→ loggedOut（再ログインが必要）
          {
            guard: 'isTokenRefreshAuthError' as const,
            target: '#lifecycle.loggedOut' as const,
          },
          // ネットワーク障害・5xx 一時エラー → retryWaiting（次の PAGE_VISIBLE で再試行）
          { target: 'retryWaiting' as const },
        ],
      },
    },
    /**
     * ネットワーク障害・5xx 一時エラーでリフレッシュ失敗した場合の待機ステート。
     * phase3.flow.appReady と parallel で動作するため、ページコンテンツは表示されたまま。
     * PAGE_VISIBLE（タブ復帰）または UNAUTHORIZED_401（API 呼び出し 401）でリトライする。
     */
    retryWaiting: {
      on: {
        PAGE_VISIBLE: 'refreshing' as const,
        UNAUTHORIZED_401: 'refreshing' as const,
      },
    },
    // 未認証ユーザーはリフレッシュ不要（安定状態）
    notRequired: {},
  },
}
